import { useState, useRef, useEffect } from 'react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const industryOptions = [
  'Insurance & Brokerages',
  'Financial & Fintech',
  'Real Estate',
  'Home Services & Trades',
  'Professional Advisory',
  'Growth-Stage Team',
  'Other',
];

export default function ContactModal({ isOpen, onClose }: ContactModalProps) {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) {
        data.append(input.name, input.value);
      }
    });
    try {
      await fetch('https://readdy.ai/api/form/d6tcdulm9vk3c28i5gbg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B2A4A]/40 dark:bg-black/60 px-4"
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div className="bg-white dark:bg-[#0F1928] rounded-xl border border-[#E5E7EB] dark:border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Modal header */}
        <div className="flex items-start justify-between p-8 pb-6 border-b border-[#E5E7EB] dark:border-white/10">
          <div>
            <h2 className="text-xl font-semibold text-[#0B2A4A] dark:text-white tracking-tight">
              Build Your System
            </h2>
            <p className="text-sm text-[#0B2A4A]/55 dark:text-white/50 mt-1 tracking-wide">
              Tell us about your business and we&apos;ll reach out within 24 hours.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#0B2A4A]/40 dark:text-white/40 hover:text-[#0B2A4A] dark:hover:text-white transition-colors cursor-pointer rounded-md hover:bg-[#F8FAFC] dark:hover:bg-white/10"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form */}
        <div className="p-8">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 flex items-center justify-center bg-[#5EEAD4]/20 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-2xl text-[#0B2A4A] dark:text-[#5EEAD4]" />
              </div>
              <h3 className="text-lg font-semibold text-[#0B2A4A] dark:text-white tracking-tight mb-2">
                Request Received
              </h3>
              <p className="text-sm text-[#0B2A4A]/55 dark:text-white/50 tracking-wide leading-relaxed">
                Our team will review your details and get back to you within 24 hours.
              </p>
              <button
                onClick={onClose}
                className="mt-6 px-6 py-2.5 bg-[#0B2A4A] dark:bg-white dark:text-[#0B2A4A] text-white text-sm font-medium tracking-wide rounded-md cursor-pointer hover:bg-[#1E3A8A] dark:hover:bg-white/90 transition-colors whitespace-nowrap"
              >
                Close
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#0B2A4A]/65 dark:text-white/55 font-medium tracking-wide mb-1.5 uppercase">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="Jane Smith"
                    className="w-full px-4 py-2.5 text-sm border border-[#E5E7EB] dark:border-white/15 rounded-md bg-[#F8FAFC] dark:bg-[#070D1B] text-[#0B2A4A] dark:text-white placeholder-[#0B2A4A]/30 dark:placeholder-white/25 focus:outline-none focus:border-[#2563EB] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-[#0B2A4A]/65 dark:text-white/55 font-medium tracking-wide mb-1.5 uppercase">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="Acme Corp"
                    className="w-full px-4 py-2.5 text-sm border border-[#E5E7EB] dark:border-white/15 rounded-md bg-[#F8FAFC] dark:bg-[#070D1B] text-[#0B2A4A] dark:text-white placeholder-[#0B2A4A]/30 dark:placeholder-white/25 focus:outline-none focus:border-[#2563EB] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#0B2A4A]/65 dark:text-white/55 font-medium tracking-wide mb-1.5 uppercase">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="jane@company.com"
                  className="w-full px-4 py-2.5 text-sm border border-[#E5E7EB] dark:border-white/15 rounded-md bg-[#F8FAFC] dark:bg-[#070D1B] text-[#0B2A4A] dark:text-white placeholder-[#0B2A4A]/30 dark:placeholder-white/25 focus:outline-none focus:border-[#2563EB] transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs text-[#0B2A4A]/65 dark:text-white/55 font-medium tracking-wide mb-1.5 uppercase">
                  Industry
                </label>
                <select
                  name="industry"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-[#E5E7EB] dark:border-white/15 rounded-md bg-[#F8FAFC] dark:bg-[#070D1B] text-[#0B2A4A] dark:text-white focus:outline-none focus:border-[#2563EB] transition-colors cursor-pointer"
                >
                  <option value="">Select your industry</option>
                  {industryOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-[#0B2A4A]/65 dark:text-white/55 font-medium tracking-wide mb-1.5 uppercase">
                  Tell us about your current challenge
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Describe where leads currently get lost or what process you want to automate..."
                  className="w-full px-4 py-2.5 text-sm border border-[#E5E7EB] dark:border-white/15 rounded-md bg-[#F8FAFC] dark:bg-[#070D1B] text-[#0B2A4A] dark:text-white placeholder-[#0B2A4A]/30 dark:placeholder-white/25 focus:outline-none focus:border-[#2563EB] transition-colors resize-none"
                />
                <p className="text-xs text-[#0B2A4A]/40 dark:text-white/30 mt-1 text-right tracking-wide">Max 500 characters</p>
              </div>

              {status === 'error' && (
                <p className="text-xs text-red-500 tracking-wide">
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3 bg-[#0B2A4A] dark:bg-[#2563EB] text-white text-sm font-medium tracking-wide rounded-md hover:bg-[#1E3A8A] dark:hover:bg-[#1E3A8A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {status === 'loading' ? 'Submitting...' : 'Start Your System'}
              </button>

              <p className="text-xs text-[#0B2A4A]/40 dark:text-white/35 text-center tracking-wide">
                No spam. No commitments. Just a structured conversation.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
